import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import './PlotViewer.css';

const PicselliaDatasetManager: React.FC = () => {
  const [datasets, setDatasets] = useState<string[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<string>('');
  const [versions, setVersions] = useState<string[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string>('');
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  // Tag sync state
  const [updatingPicselliaTags, setUpdatingPicselliaTags] = useState(false);
  const [syncingPostgresFromPicsellia, setSyncingPostgresFromPicsellia] = useState(false);
  const [tagSyncError, setTagSyncError] = useState<string>('');
  const [tagSyncSuccess, setTagSyncSuccess] = useState<string>('');
  const [jobs, setJobs] = useState<Array<{
    id: string;
    label: string;
    message: string;
    completed: number;
    total: number | null;
  }>>([]);

  const pollPicselliaJob = (
    jobId: string,
    label: string,
    onComplete: (data: {
      status: string;
      message: string;
      success?: boolean;
      error?: string;
      updated?: number;
      skipped?: number;
      not_found?: number;
      record_count?: number;
    }) => void,
    onPollError: (message: string) => void
  ) => {
    setJobs((current) => [
      ...current,
      { id: jobId, label, message: 'Queued', completed: 0, total: null },
    ]);
    let missing = 0;
    const pollInterval = window.setInterval(async () => {
      try {
        const response = await api.picsellia.getUploadJobStatus(jobId);
        missing = 0;
        const status = response.data.status;
        setJobs((current) => current.map((job) => (
          job.id === jobId
            ? {
              ...job,
              message: response.data.message,
              completed: response.data.completed ?? 0,
              total: response.data.total ?? null,
            }
            : job
        )));

        if (status === 'completed' || status === 'failed') {
          window.clearInterval(pollInterval);
          setJobs((current) => current.filter((job) => job.id !== jobId));
          onComplete(response.data);
        }
      } catch (err: unknown) {
        const httpStatus = (err as { response?: { status?: number } }).response?.status;
        if (httpStatus === 404 && missing < 5) {
          missing += 1;
          return;
        }
        window.clearInterval(pollInterval);
        setJobs((current) => current.filter((job) => job.id !== jobId));
        const message = err instanceof Error ? err.message : 'Unknown error';
        onPollError(message);
      }
    }, 2000);
  };

  const loadDatasets = useCallback(async () => {
    setLoadingDatasets(true);
    setError('');
    try {
      const response = await api.picsellia.getDatasets();
      setDatasets(response.data.datasets);
      setSelectedDataset((current) => current || response.data.datasets[0] || '');
    } catch (err: any) {
      setError(`Failed to load datasets: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoadingDatasets(false);
    }
  }, []);

  // Load datasets on component mount
  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  // Load versions when dataset is selected
  useEffect(() => {
    if (selectedDataset) {
      loadVersions(selectedDataset);
    } else {
      setVersions([]);
      setSelectedVersion('');
    }
  }, [selectedDataset]);

  const loadVersions = async (datasetName: string) => {
    setLoadingVersions(true);
    setError('');
    setSelectedVersion('');
    try {
      const response = await api.picsellia.listDatasetVersions(datasetName);
      setVersions(response.data.versions);
      if (response.data.versions.length > 0) {
        setSelectedVersion(response.data.versions[0]);
      }
    } catch (err: any) {
      setError(`Failed to load versions: ${err.response?.data?.detail || err.message}`);
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleIngest = async () => {
    if (!selectedDataset || !selectedVersion) {
      setError('Please select both a dataset and a version');
      return;
    }

    setIngesting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.picsellia.ingestDatasetVersion({
        dataset_name: selectedDataset,
        version: selectedVersion,
      });
      setIngesting(false);
      setSuccessMessage(
        response.data.message ||
          `Ingest job queued for '${selectedDataset}' version '${selectedVersion}'`
      );
      pollPicselliaJob(
        response.data.job_id,
        `Ingest ${selectedDataset} ${selectedVersion}`,
        (data) => {
          if (data.status === 'completed' && data.success !== false) {
            setSuccessMessage(data.message || `Successfully ingested '${selectedDataset}' (${selectedVersion})`);
            setError('');
          } else {
            setError(data.error || data.message || 'Failed to ingest dataset version');
            setSuccessMessage('');
          }
        },
        (message) => setError(`Failed to check ingest job status: ${message}`)
      );
    } catch (err: any) {
      setIngesting(false);
      setError(`Failed to ingest dataset: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleUpdatePicselliaTags = async () => {
    if (!selectedDataset || !selectedVersion) {
      setTagSyncError('Select a dataset and version first');
      return;
    }
    setTagSyncError('');
    setTagSyncSuccess('');
    setUpdatingPicselliaTags(true);
    try {
      const response = await api.picsellia.updatePicselliaTags({
        dataset_name: selectedDataset,
        version: selectedVersion,
        datalake_name: 'la-poseidon-datasets',
      });
      setUpdatingPicselliaTags(false);
      setTagSyncSuccess(response.data.message || 'Picsellia tag update job queued');
      pollPicselliaJob(
        response.data.job_id,
        `Update Picsellia tags ${selectedDataset} ${selectedVersion}`,
        (data) => {
          if (data.status === 'completed' && data.success !== false) {
            setTagSyncSuccess(
              data.message ||
                `Picsellia tags updated: ${data.updated ?? 0} updated, ${data.skipped ?? 0} skipped, ${data.not_found ?? 0} not in Picsellia`
            );
            setTagSyncError('');
          } else {
            setTagSyncError(data.error || data.message || 'Failed to update Picsellia tags');
            setTagSyncSuccess('');
          }
        },
        (message) => setTagSyncError(`Failed to check Picsellia tag update status: ${message}`)
      );
    } catch (err: any) {
      setUpdatingPicselliaTags(false);
      setTagSyncError(
        `Failed to update Picsellia tags: ${err.response?.data?.detail || err.message}`
      );
    }
  };

  const handleSyncPostgresTagsFromPicsellia = async () => {
    if (!selectedDataset || !selectedVersion) {
      setTagSyncError('Select a dataset and version first');
      return;
    }
    setTagSyncError('');
    setTagSyncSuccess('');
    setSyncingPostgresFromPicsellia(true);
    try {
      const response = await api.picsellia.syncPostgresTagsFromPicsellia({
        dataset_name: selectedDataset,
        version: selectedVersion,
      });
      setSyncingPostgresFromPicsellia(false);
      setTagSyncSuccess(response.data.message || 'Postgres tag sync job queued');
      pollPicselliaJob(
        response.data.job_id,
        `Sync Postgres tags ${selectedDataset} ${selectedVersion}`,
        (data) => {
          if (data.status === 'completed' && data.success !== false) {
            setTagSyncSuccess(
              data.message ||
                `Synced Picsellia asset tags to Postgres: ${data.updated ?? 0} updated, ${data.skipped ?? 0} skipped, ${data.not_found ?? 0} not found`
            );
            setTagSyncError('');
          } else {
            setTagSyncError(data.error || data.message || 'Failed to sync Postgres tags from Picsellia');
            setTagSyncSuccess('');
          }
        },
        (message) => setTagSyncError(`Failed to check Postgres tag sync status: ${message}`)
      );
    } catch (err: any) {
      setSyncingPostgresFromPicsellia(false);
      setTagSyncError(
        `Failed to sync Postgres tags from Picsellia: ${err.response?.data?.detail || err.message}`
      );
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '2rem' }}>Picsellia Dataset Manager</h2>
      {jobs.length > 0 && (
        <div style={{ marginTop: '-1rem', marginBottom: '1rem', display: 'grid', gap: '0.75rem' }}>
          {jobs.map((job) => {
            const pct = job.total ? Math.min(100, Math.round((job.completed / job.total) * 100)) : 0;
            return (
              <div key={job.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.85rem', color: '#555', marginBottom: 4 }}>
                  <span>{job.label}</span>
                  <span>{job.total ? `${job.completed} / ${job.total}` : 'Starting'}</span>
                </div>
                <div style={{ height: 8, background: '#e9ecef', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#007bff' }} />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#777', marginTop: 4 }}>{job.message}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error and Success Messages */}
      {error && (
        <div
          style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            color: '#c33',
          }}
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          style={{
            padding: '1rem',
            marginBottom: '1rem',
            backgroundColor: '#efe',
            border: '1px solid #cfc',
            borderRadius: '4px',
            color: '#3c3',
          }}
        >
          {successMessage}
        </div>
      )}

      {/* Dataset Selection */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
          Dataset:
        </label>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select
            value={selectedDataset}
            onChange={(e) => setSelectedDataset(e.target.value)}
            disabled={loadingDatasets}
            style={{
              padding: '0.5rem',
              fontSize: '1rem',
              minWidth: '300px',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}
          >
            <option value="">Select a dataset...</option>
            {datasets.map((dataset) => (
              <option key={dataset} value={dataset}>
                {dataset}
              </option>
            ))}
          </select>
          <button
            onClick={loadDatasets}
            disabled={loadingDatasets}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loadingDatasets ? 'not-allowed' : 'pointer',
              opacity: loadingDatasets ? 0.6 : 1,
            }}
          >
            {loadingDatasets ? 'Loading...' : 'Refresh'}
          </button>
        </div>
        {loadingDatasets && <p style={{ marginTop: '0.5rem', color: '#666' }}>Loading datasets...</p>}
      </div>

      {/* Version Selection */}
      {selectedDataset && (
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Version:
          </label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(e.target.value)}
              disabled={loadingVersions || !selectedDataset}
              style={{
                padding: '0.5rem',
                fontSize: '1rem',
                minWidth: '300px',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            >
              <option value="">Select a version...</option>
              {versions.map((version) => (
                <option key={version} value={version}>
                  {version}
                </option>
              ))}
            </select>
            <button
              onClick={() => loadVersions(selectedDataset)}
              disabled={loadingVersions || !selectedDataset}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loadingVersions || !selectedDataset ? 'not-allowed' : 'pointer',
                opacity: loadingVersions || !selectedDataset ? 0.6 : 1,
              }}
            >
              {loadingVersions ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          {loadingVersions && <p style={{ marginTop: '0.5rem', color: '#666' }}>Loading versions...</p>}
          {!loadingVersions && versions.length === 0 && selectedDataset && (
            <p style={{ marginTop: '0.5rem', color: '#666' }}>No versions found for this dataset.</p>
          )}
        </div>
      )}

      {/* Ingest Button */}
      {selectedDataset && selectedVersion && (
        <div style={{ marginTop: '2rem' }}>
          <button
            onClick={handleIngest}
            disabled={ingesting || !selectedDataset || !selectedVersion}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1.1rem',
              backgroundColor: ingesting ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: ingesting || !selectedDataset || !selectedVersion ? 'not-allowed' : 'pointer',
              opacity: ingesting || !selectedDataset || !selectedVersion ? 0.6 : 1,
            }}
          >
            {ingesting ? 'Ingesting...' : `Ingest Dataset: ${selectedDataset} (${selectedVersion})`}
          </button>
          {ingesting && (
            <p style={{ marginTop: '0.5rem', color: '#666' }}>
              Submitting ingest job…
            </p>
          )}
        </div>
      )}

      {/* Tag Sync Section */}
      <div
        style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#f0f7ff',
          borderRadius: '8px',
          border: '1px solid #c5d9f0',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Picsellia & Postgres tag sync</h3>
        <p style={{ marginBottom: '1rem', color: '#444', lineHeight: 1.5 }}>
          Sync tags for the <strong>selected dataset and version</strong> above. Update Picsellia
          syncs tags/metadata from postgres to existing Picsellia objects for that version’s assets.
          <strong> Sync Postgres from Picsellia</strong> adds asset tags from the dataset version into the metadata DB (merge only; no tags removed).
        </p>
        {tagSyncError && (
          <div
            style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '4px',
              color: '#c33',
            }}
          >
            {tagSyncError}
          </div>
        )}
        {tagSyncSuccess && (
          <div
            style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              backgroundColor: '#efe',
              border: '1px solid #cfc',
              borderRadius: '4px',
              color: '#3c3',
            }}
          >
            {tagSyncSuccess}
          </div>
        )}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleUpdatePicselliaTags}
            disabled={
              updatingPicselliaTags ||
              syncingPostgresFromPicsellia ||
              !selectedDataset ||
              !selectedVersion
            }
            style={{
              padding: '0.6rem 1.25rem',
              fontSize: '1rem',
              backgroundColor:
                updatingPicselliaTags || !selectedDataset || !selectedVersion ? '#6c757d' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor:
                updatingPicselliaTags || syncingPostgresFromPicsellia || !selectedDataset || !selectedVersion
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                updatingPicselliaTags || syncingPostgresFromPicsellia || !selectedDataset || !selectedVersion
                  ? 0.6
                  : 1,
            }}
          >
            {updatingPicselliaTags
              ? 'Updating Picsellia tags…'
              : `Update Picsellia tags${selectedDataset && selectedVersion ? ` (${selectedDataset} / ${selectedVersion})` : ''}`}
          </button>
          <button
            onClick={handleSyncPostgresTagsFromPicsellia}
            disabled={
              updatingPicselliaTags ||
              syncingPostgresFromPicsellia ||
              !selectedDataset ||
              !selectedVersion
            }
            style={{
              padding: '0.6rem 1.25rem',
              fontSize: '1rem',
              backgroundColor:
                syncingPostgresFromPicsellia || !selectedDataset || !selectedVersion ? '#6c757d' : '#0a5f0a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor:
                updatingPicselliaTags || syncingPostgresFromPicsellia || !selectedDataset || !selectedVersion
                  ? 'not-allowed'
                  : 'pointer',
              opacity:
                updatingPicselliaTags || syncingPostgresFromPicsellia || !selectedDataset || !selectedVersion
                  ? 0.6
                  : 1,
            }}
          >
            {syncingPostgresFromPicsellia
              ? 'Syncing Postgres from Picsellia…'
              : `Sync Postgres from Picsellia${selectedDataset && selectedVersion ? ` (${selectedDataset} / ${selectedVersion})` : ''}`}
          </button>
        </div>
      </div>

      {/* Info Section */}
      <div
        style={{
          marginTop: '3rem',
          padding: '1.5rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          border: '1px solid #dee2e6',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>About Dataset Ingestion</h3>
        <p style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
          This tool allows you to ingest Picsellia dataset versions into the metadata database.
        </p>
        <p style={{ marginBottom: '0.5rem', lineHeight: '1.6' }}>
          When you ingest a dataset version, the system will:
        </p>
        <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
          <li>Extract all assets (images) from the selected dataset version</li>
          <li>Format the data with annotations, bounding boxes, and classifications</li>
          <li>Send the records to the ingestion API (Lambda) for database insertion</li>
        </ul>
        <p style={{ marginTop: '1rem', marginBottom: 0, lineHeight: '1.6', color: '#666' }}>
          <strong>Note:</strong> This process may take some time depending on the size of the dataset.
        </p>
      </div>
    </div>
  );
};

export default PicselliaDatasetManager;
